import React, { useState } from 'react';
import config from '../../config/config';
import {
  resolveServicePage,
  servicePageIds,
  serviceRouteName,
} from '../../config/configServicePages';
import { t } from '../../util/messages';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
  ServiceGraphic,
  ServiceIcon,
} from '../../components';
import { TopbarContainer } from '..';
import SectionPortfolio from '../LandingPage/SectionPortfolio/SectionPortfolio';
import css from './ServicesPage.module.css';
/*
 * The service pages' layout, reused rather than restated. See the note at the
 * top of ServicesPage.module.css for why this is an import and not `composes`
 * or a copy.
 */
import shared from '../ServicePage/ServicePage.module.css';

/**
 * The services hub.
 *
 * WHAT IT FIXES
 * -------------
 * Three things were leaning on a page that did not exist. The topbar's
 * Services link went to the `/#services` fragment — the pattern every other
 * part of this work has been moving away from. Each service page's breadcrumb
 * read "Home / <service>" because there was no parent to name. And the footer
 * carried all eleven services because nothing else listed them in one place.
 *
 * It is also the page someone lands on from "software development services",
 * which is a real query that no page here previously answered.
 *
 * The grid is the registry in its configured order, so the two AI entries
 * lead — the same ordering decision the landing page makes, for the same
 * reason — and a service added to config appears here with no edit.
 */
const ServicesPage = () => {
  const [scrollingDisabled, setScrollingDisabled] = useState(false);

  /* SectionPortfolio's cards carry data-reveal; this is what arms them. */
  useScrollReveal();

  const onManageDisableScrolling = (_id, disabled = false) => {
    setScrollingDisabled(disabled);
  };

  const { siteTitle, canonicalRootURL, configProcess } = config;

  const pages = servicePageIds.map(resolveServicePage);
  const url = `${canonicalRootURL}/services`;
  const title = t('ServicesPage.title', { siteTitle });
  const description = t('ServicesPage.schemaDescription');


  return (
    <Page
      title={title}
      description={description}
      scrollingDisabled={scrollingDisabled}
      schema={[
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          '@id': url,
          name: title,
          description,
          url,
          isPartOf: { '@id': `${canonicalRootURL}/#website` },
          about: { '@id': `${canonicalRootURL}/#organization` },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: siteTitle, item: canonicalRootURL },
              { '@type': 'ListItem', position: 2, name: t('ServicesPage.heading'), item: url },
            ],
          },
          /*
           * Every service, in order, each pointing at its own page. This is
           * the machine-readable form of what the grid below shows, and it
           * is the reason a hub earns its place over a list in a footer.
           */
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: pages.map((page, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: page.title,
              url: `${canonicalRootURL}/services/${page.slug}`,
            })),
          },
        },
      ]}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer onManageDisableScrolling={onManageDisableScrolling} />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain>
          <div className={shared.root}>
            {/* ---- Hero ------------------------------------------------ */}
            <header className={shared.header}>
              <div className={shared.headerCopy}>
                <nav className={shared.breadcrumb} aria-label="Breadcrumb">
                  <NamedLink name="LandingPage">{t('ServicesPage.breadcrumbHome')}</NamedLink>
                  <span aria-hidden="true">/</span>
                  <span>{t('ServicesPage.heading')}</span>
                </nav>

                <h1 className={shared.heading}>{t('ServicesPage.heading')}</h1>
                <p className={shared.intro}>{t('ServicesPage.intro')}</p>

                <div className={shared.heroActions}>
                  <NamedLink className={shared.cta} name="LandingPage" to={{ hash: '#contact-us' }}>
                    {t('ServicesPage.ctaAction')}
                  </NamedLink>
                  <NamedLink
                    className={shared.ctaSecondary}
                    name="LandingPage"
                    to={{ hash: '#portfolio' }}
                  >
                    {t('ServicesPage.ctaSecondary')}
                  </NamedLink>
                </div>
              </div>

              <div className={shared.headerArt}>
                <ServiceGraphic serviceId="servicesIndex" className={shared.graphic} />
              </div>
            </header>

            {/* ---- Every service --------------------------------------- */}
            <section className={shared.section} aria-labelledby="services-all">
              <h2 id="services-all" className={shared.sectionHeading}>
                {t('ServicesPage.gridHeading')}
              </h2>

              <ul className={css.grid}>
                {pages.map(page => (
                  <li key={page.id}>
                    <NamedLink
                      className={css.card}
                      name={serviceRouteName(page.id)}
                      title={page.title}
                    >
                      <ServiceIcon serviceId={page.id} className={css.cardIcon} />
                      <h3 className={css.cardTitle}>{page.title}</h3>
                      <p className={css.cardBody}>{page.description}</p>

                      {page.tags.length > 0 && (
                        <ul className={css.cardTags}>
                          {page.tags.slice(0, 3).map(tag => (
                            <li key={tag}>{tag}</li>
                          ))}
                        </ul>
                      )}

                      <span className={css.cardMore} aria-hidden="true">
                        {t('ServicesPage.cardMore')}
                      </span>
                    </NamedLink>
                  </li>
                ))}
              </ul>
            </section>

            {/* ---- How we work ----------------------------------------- */}
            <section className={shared.section} aria-labelledby="services-process">
              <h2 id="services-process" className={shared.sectionHeading}>
                {t('ServicesPage.processHeading')}
              </h2>
              <ol className={shared.processList}>
                {configProcess.map((step, index) => (
                  <li key={step.id} className={shared.processStep}>
                    <span className={shared.processHead}>
                      <img
                        className={shared.processIcon}
                        src={step.icon}
                        alt=""
                        width="28"
                        height="31"
                        loading="lazy"
                      />
                      <span className={shared.processNumber} aria-hidden="true">
                        {index + 1}
                      </span>
                    </span>
                    <h3 className={shared.processTitle}>{step.title}</h3>
                    <p className={shared.processBody}>{step.description}</p>
                  </li>
                ))}
              </ol>
            </section>

            {/*
              * The landing page's own portfolio section, not a second
              * version of it. It brings its own heading, the featured
              * treatment for the first project and the per-project metrics —
              * none of which the hand-rolled grid here had, and all of which
              * would have had to be kept in step by hand.
              */}
            <SectionPortfolio className={css.portfolio} />

            {/* ---- Closing call to action ------------------------------ */}
            <aside className={shared.closing}>
              <h2 className={shared.closingHeading}>{t('ServicesPage.closingHeading')}</h2>
              <p className={shared.closingBody}>{t('ServicesPage.closingBody')}</p>
              <NamedLink className={shared.cta} name="LandingPage" to={{ hash: '#contact-us' }}>
                {t('ServicesPage.ctaAction')}
              </NamedLink>
            </aside>
          </div>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <div className={shared.footerContainer}>
            <Footer />
          </div>
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ServicesPage;
